import { api, ApiError } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import type { DeleteAccountResponse, LeaveWorkspaceResponse, MessageResponse, Profile } from './types'

export const profileApi = {
  get: () => api.get<Profile>('/api/profile'),

  updateName: (name: string) => api.put<MessageResponse>('/api/profile', { name }),

  // Profile image upload - multipart/form-data. Bypasses the JSON
  // helper because `api.post` always sets Content-Type: application/json.
  uploadImage: async (file: File): Promise<MessageResponse> => {
    const fd = new FormData()
    fd.append('image', file)
    const token = tokenManager.get()
    const res = await fetch('/api/profile/image', {
      method: 'POST',
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    })
    if (res.status === 401) {
      const ok = await tokenManager.refreshNow()
      if (ok) {
        const retryToken = tokenManager.get()
        const retry = await fetch('/api/profile/image', {
          method: 'POST',
          credentials: 'same-origin',
          headers: retryToken ? { Authorization: `Bearer ${retryToken}` } : undefined,
          body: fd,
        })
        if (!retry.ok) {
          let body: unknown
          try { body = await retry.json() } catch { /* ignore */ }
          throw new ApiError(`Upload failed (${retry.status})`, retry.status, body)
        }
        return (await retry.json()) as MessageResponse
      }
    }
    if (!res.ok) {
      let body: unknown
      try { body = await res.json() } catch { /* ignore */ }
      throw new ApiError(`Upload failed (${res.status})`, res.status, body)
    }
    return (await res.json()) as MessageResponse
  },

  requestDeleteCode: () => api.post<MessageResponse>('/api/auth/request-delete-code'),

  deleteAccount: (verificationCode: string) =>
    api.post<DeleteAccountResponse>('/api/auth/delete-account', { verificationCode }),

  leaveCurrentWorkspace: () =>
    api.post<LeaveWorkspaceResponse>('/api/tenant/members/me/leave'),
}
